import { ref } from 'vue';
import { http } from '@/system/http';
import SpeechmaticsTranscribe from 'Components/global/ai/transcription/speechmatics';
import { TranscriptionProvider } from 'Components/global/ai/constants';
import useAudioDevices from './useAudioDevices';
import useTranscriptionSettings from './useTranscriptionSettings';

export default function useTranscriptionService() {
    const { selectedAudioDevice, onAudioDenied } = useAudioDevices();
    const {
        language: selectedLanguage,
        provider: transcriptionProvider,
        defaultLanguage
    } = useTranscriptionSettings();

    const isRecording = ref(false);
    const unstableChunk = ref('');
    const awsTranscriptionInitialized = ref(false);
    const speechmaticsTranscriptionInitialized = ref(false);
    const speechmaticsTranscribe = ref(null);
    const speechmaticsLatestChunkTime = ref(null);

    // Callback handlers
    let onChunkReadyCallback = null;
    let onUnstableChunkCallback = null;

    async function fetchCredentials(provider) {
        const { data } = await http.get(`/ai-transcriptions/credentials?provider=${provider}`);
        return data;
    }

    // AWS Transcription methods
    async function initAwsTranscription() {
        if (awsTranscriptionInitialized.value) {
            return;
        }

        const credentials = await fetchCredentials(TranscriptionProvider.Aws);

        window.Transcribe.initialise(credentials.aws_key, credentials.aws_secret, credentials.aws_session_token, {
            awsRegion: 'eu-west-1',
            unstableChunkReadyCallback: (chunk) => {
                unstableChunk.value = chunk;
                if (onUnstableChunkCallback) {
                    onUnstableChunkCallback(chunk);
                }
            },
            language: selectedLanguage.value?.id || defaultLanguage.value,
        });

        awsTranscriptionInitialized.value = true;
    }

    function destroyAwsTranscription() {
        if (!awsTranscriptionInitialized.value) {
            return;
        }

        window.Transcribe.destroy();
        awsTranscriptionInitialized.value = false;
    }

    async function startAwsRecording() {
        await initAwsTranscription();

        // We don't await this call, because it will only be resolved when recording is stopped.
        window.Transcribe.startRecording(
            handleTranscriptChunkReady,
            selectedLanguage.value?.id || defaultLanguage.value,
            selectedAudioDevice.value?.deviceId,
        )
    }

    function stopAwsRecording() {
        window.Transcribe.stopRecording();
        if (unstableChunk.value.length > 0) {
            handleTranscriptChunkReady(unstableChunk.value + ' ');
            unstableChunk.value = '';
        }
    }

    // Speechmatics Transcription methods
    async function initSpeechmaticsTranscription() {
        if (speechmaticsTranscriptionInitialized.value && speechmaticsTranscribe.value) {
            return;
        }

        speechmaticsTranscribe.value = new SpeechmaticsTranscribe(
            () => fetchCredentials(TranscriptionProvider.Speechmatics)
        );

        speechmaticsTranscribe.value.onConcurrencyUpdate = (data) => {
            transcriptionLog('concurrency', data);
        }

        speechmaticsTranscribe.value.onTranscriptionUpdate = (data, error) => {
            if (error) {
                console.error(error);
                return;
            }

            if (!data) {
                return;
            }

            const { type, text, speaker } = data;
            const now = Date.now();
            const timeElapsed = now - (speechmaticsLatestChunkTime.value || 0);
            speechmaticsLatestChunkTime.value = now;

            if (type === 'partial') {
                unstableChunk.value = text;
                if (onUnstableChunkCallback) {
                    if (timeElapsed > 3000) {
                        onUnstableChunkCallback(text, true);
                    } else {
                        onUnstableChunkCallback(text);
                    }
                }
            } else {
                if (onChunkReadyCallback) {
                    // For new chunks or when enough time has passed
                    if (timeElapsed > 3000) {
                        onChunkReadyCallback(text, true); // true indicates a new chunk
                    } else {
                        onChunkReadyCallback(text, false); // false indicates appending to existing chunk
                    }
                }
                unstableChunk.value = '';
            }
        };

        speechmaticsTranscriptionInitialized.value = true;
    }

    function destroySpeechmaticsTranscription() {
        if (!speechmaticsTranscriptionInitialized.value) {
            return;
        }

        speechmaticsTranscribe.value.stopRecording();
        speechmaticsTranscribe.value = null;
        speechmaticsTranscriptionInitialized.value = false;
    }

    async function startSpeechmaticsRecording() {
        if (!speechmaticsTranscriptionInitialized.value) {
            await initSpeechmaticsTranscription();
        }

        speechmaticsTranscribe.value.setSelectedDevice(selectedAudioDevice.value.deviceId);
        speechmaticsTranscribe.value.startRecording(selectedLanguage.value.country);
    }

    function stopSpeechmaticsRecording() {
        speechmaticsTranscribe.value?.stopRecording();
    }

    // Main interface methods
    function handleTranscriptChunkReady(transcriptChunk, isNewChunk = true) {
        if (onChunkReadyCallback) {
            onChunkReadyCallback(transcriptChunk, isNewChunk);
        }
        unstableChunk.value = '';
    }

    async function startRecording() {
        if (isRecording.value) {
            return;
        }

        try {
            if (transcriptionProvider.value === TranscriptionProvider.Aws) {
                await startAwsRecording();
            } else if (transcriptionProvider.value === TranscriptionProvider.Speechmatics) {
                await startSpeechmaticsRecording();
            }

            isRecording.value = true;
            return true;
        } catch (error) {
            isRecording.value = false;

            if (error.name === 'NotAllowedError') {
                onAudioDenied();
                destroyAwsTranscription();
                destroySpeechmaticsTranscription();
            } else {
                throw error;
            }
            return false;
        }
    }

    function stopRecording() {
        if (transcriptionProvider.value === TranscriptionProvider.Aws) {
            stopAwsRecording();
        } else if (transcriptionProvider.value === TranscriptionProvider.Speechmatics) {
            stopSpeechmaticsRecording();
        }

        isRecording.value = false;
    }

    function cleanup() {
        destroyAwsTranscription();
        destroySpeechmaticsTranscription();
        isRecording.value = false;
        unstableChunk.value = '';
    }

    function transcriptionLog(type, data = {}) {
        http.post('/app/ai/transcription-log', { type, data });
    }

    function setCallbacks({ onChunkReady, onUnstableChunk }) {
        onChunkReadyCallback = onChunkReady;
        onUnstableChunkCallback = onUnstableChunk;
    }

    return {
        isRecording,
        unstableChunk,

        // Core methods
        startRecording,
        stopRecording,
        cleanup,

        // Callback setters
        setCallbacks,

        // Exposed for testing/debugging
        transcriptionProvider,
        fetchCredentials,
    };
}
